
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../src/components/ui/card.jsx";
import { Badge } from "../../src/components/ui/badge.jsx";
import { MapPin, Lock, Globe } from "lucide-react";

const presets = [
  // Wingecarribee address presets
  {
    id: "wsc_12_betty_close",
    title: "Wingecarribee",
    description: "12 Betty Close Bowral NSW, 2576",
    icon: MapPin,
    color: "green",
    config: {
      preset_type: "address_preset",
      org_id: "7d0236c7-6e3d-4194-9963-460cf7c30ece", // WSC dev
      state: "qa",
      address: "12 Betty Close Bowral NSW, 2576"
    }
  },
  {
    id: "wsc_39_southey_st",
    title: "Wingecarribee",
    description: "39 Southey St, Mittagong NSW, 2575",
    icon: MapPin,
    color: "green",
    config: {
      preset_type: "address_preset",
      org_id: "7d0236c7-6e3d-4194-9963-460cf7c30ece",
      state: "qa",
      address: "39 Southey St, Mittagong NSW, 2575"
    }
  },
  {
    id: "wsc_6_patuna_ave",
    title: "Wingecarribee",
    description: "6 Patuna Ave, Moss Vale NSW 2577",
    icon: MapPin,
    color: "green",
    config: {
      preset_type: "address_preset",
      org_id: "7d0236c7-6e3d-4194-9963-460cf7c30ece",
      state: "qa",
      address: "6 Patuna Ave, Moss Vale NSW 2577"
    }
  },
  // Cumberland address presets
  {
    id: "ccc_12_lisgar_st",
    title: "Cumberland",
    description: "12 Lisgar Street, Granville NSW, 2142",
    icon: MapPin,
    color: "orange",
    config: {
      preset_type: "address_preset",
      org_id: "8384a975-6513-4f07-b3a4-86391e2773af", // CCC dev
      state: "qa",
      address: "12 Lisgar Street, Granville NSW, 2142"
    }
  },
  {
    id: "ccc_2_marin_place",
    title: "Cumberland",
    description: "2 Marin Place, Merrylands NSW, 2160",
    icon: MapPin,
    color: "orange",
    config: {
      preset_type: "address_preset",
      org_id: "8384a975-6513-4f07-b3a4-86391e2773af",
      state: "qa",
      address: "2 Marin Place, Merrylands NSW, 2160"
    }
  },
  {
    id: "ccc_50_monitor_rd",
    title: "Cumberland",
    description: "50 Monitor Rd, Merrylands NSW 2160",
    icon: MapPin,
    color: "orange",
    config: {
      preset_type: "address_preset",
      org_id: "8384a975-6513-4f07-b3a4-86391e2773af",
      state: "qa",
      address: "50 Monitor Rd, Merrylands NSW 2160"
    }
  },
  // Blacktown address presets
  {
    id: "bcc_186_blacktown_rd",
    title: "Blacktown",
    description: "186 Blacktown Road, Blacktown, NSW 2148",
    icon: MapPin,
    color: "purple",
    config: {
      preset_type: "address_preset",
      org_id: "4819a244-9405-4202-af83-3055a0236624", // BCC dev
      state: "qa",
      address: "186 Blacktown Road, Blacktown, NSW 2148"
    }
  },
  {
    id: "bcc_8_bertie_st",
    title: "Blacktown",
    description: "8 Bertie Street, Marsden Park, NSW 2765",
    icon: MapPin,
    color: "purple",
    config: {
      preset_type: "address_preset",
      org_id: "4819a244-9405-4202-af83-3055a0236624",
      state: "qa",
      address: "8 Bertie Street, Marsden Park, NSW 2765"
    }
  },
  {
    id: "bcc_20_barnier_dr",
    title: "Blacktown",
    description: "20 Barnier Drive, Quakers Hill, NSW 2763",
    icon: MapPin,
    color: "purple",
    config: {
      preset_type: "address_preset",
      org_id: "4819a244-9405-4202-af83-3055a0236624",
      state: "qa",
      address: "20 Barnier Drive, Quakers Hill, NSW 2763"
    }
  },
];

export default function PresetSelector({ onSelect, currentConfig, currentPresetType, onScenarioAuthChange }) {
  const [selectedState, setSelectedState] = React.useState(currentConfig?.state || "");
  const [selectedAuth, setSelectedAuth] = React.useState(undefined);

  const applyConfigPreset = (partial) => {
    const next = {
      ...currentConfig,
      ...partial,
      workflow: {
        ...(currentConfig.workflow || {}),
        next_action: "",
      },
      project: {
        ...(currentConfig.project || {}),
      },
    };
    onSelect(next, currentPresetType || "custom");
  };
  const handlePresetSelect = (preset) => {
    const presetAddress = preset.config.address || "";
    const baseConfig = {
      org_id: preset.config.org_id || "",
      project_id: preset.config.project_id || "",
      conversation_id: preset.config.conversation_id || "",
      country: "Australia",
      first_name: preset.config.first_name || "",
      last_name: preset.config.last_name || "",
      email: preset.config.email || "",
      state: preset.config.state || "",
      user_id: preset.config.user_id || "",
      workflow: {
        next_action: presetAddress ? "" : (preset.config.state === "qa" ? "" : "enter_address"),
        execution_logs: {},
        intent: "property_development_inquiry"
      },
      project: {
        council_name: preset.config.org_id === "4819a244-9405-4202-af83-3055a0236624" ? "Blacktown City Council" :
                     preset.config.org_id === "7d0236c7-6e3d-4194-9963-460cf7c30ece" ? "Wingecarribee Shire Council" :
                     preset.config.org_id === "8384a975-6513-4f07-b3a4-86391e2773af" ? "Cumberland City Council" : "",
        address: presetAddress,
        zone: "",
        zone_description: "",
        prop_id: "",
        active_precinct: "",
        application_name: "",
        development_type: "",
        planning_pathway: "",
        lot_width: "",
        lot_size: "",
        minimum_lot_size: "",
        project_size: "",
        building_height: "",
        maximum_building_height: "",
        sub_division: "",
        front_setback: "",
        side_setback: "",
        rear_setback: "",
        is_bushfire_zone: "",
        is_flood_zone: "",
        is_heritage_zone: "",
        matched_pathways: [],
        failed_pathways: [],
        additional_criteria: "",
        flags: {},
        dwelling_density: "",
        documents: [],
        uploaded_documents: [],
        is_corner_lot: false,
        is_battle_axe_lot: false
      },
      policies: {
        state: {
          SEP: "",
          SEP_public_link: "",
          pol_min_lot_size: "",
          pol_min_lot_width: "",
          pol_max_FSR: "",
          pol_max_building_height: "",
          pol_max_building_depth: "",
          pol_max_building_separation: "",
          pol_max_site_coverage: "",
          pol_allowed_use_types: "",
          pol_basix_energy_target: "",
          pol_basix_water_target: "",
          pol_bushfire_attack_level: "",
          pol_coastal_vulnerability: "",
          pol_foreshore_building_line: "",
          pol_front_setback: "",
          pol_gsyd_tree_canopy_2019: "",
          pol_gsyd_tree_canopy_2022: "",
          pol_housing_prod_contribution: "",
          pol_is_flood_prone: "",
          pol_is_heritage_item: "",
          pol_land_reservation_acquisition: "",
          pol_landscaped_pct: "",
          pol_privacy_separation: "",
          pol_rear_setback: "",
          pol_regional_plan_boundary: "",
          pol_salinity: "",
          pol_side_setback: "",
          pol_solar_access: "",
          pol_acid_sulfate_class: ""
        },
        local: {
          DCP: "",
          DCP_public_link: "",
          LEP: "",
          LEP_public_link: "",
          pol_min_lot_size: "",
          pol_max_FSR: "",
          pol_max_building_depth: "",
          pol_max_building_height: "",
          pol_max_building_separation: "",
          pol_max_site_coverage: "",
          pol_is_heritage_area: "",
          pol_land_zone: "",
          pol_landscaped_pct: "",
          pol_land_council: "",
          pol_provisions: "",
          pol_permitted_use: ""
        }
      }
    };

    const scenarioAuth = selectedAuth === 'logged_in' ? 'signed_in' : 'anonymous';
    onSelect(baseConfig, preset.config.preset_type, scenarioAuth);
  };

  // Reflect auth badge selection upstream immediately
  React.useEffect(() => {
    if (!onScenarioAuthChange) return;
    if (!selectedAuth) return;
    const scenarioAuth = selectedAuth === 'logged_in' ? 'signed_in' : 'anonymous';
    onScenarioAuthChange(scenarioAuth);
  }, [selectedAuth]);

  const getColorClasses = (color) => {
    const colors = {
      purple: "border-purple-200 hover:border-purple-300 bg-purple-50 hover:bg-purple-100 dark:border-purple-800 dark:hover:border-purple-700 dark:bg-purple-950/25 dark:hover:bg-purple-900/30",
      orange: "border-orange-200 hover:border-orange-300 bg-orange-50 hover:bg-orange-100 dark:border-orange-800 dark:hover:border-orange-700 dark:bg-orange-950/25 dark:hover:bg-orange-900/30",
      green: "border-green-200 hover:border-green-300 bg-green-50 hover:bg-green-100 dark:border-green-800 dark:hover:border-green-700 dark:bg-green-950/25 dark:hover:bg-green-900/30",
      slate: "border-slate-200 hover:border-slate-300 bg-slate-50 hover:bg-slate-100 dark:border-slate-700 dark:hover:border-slate-600 dark:bg-slate-900/50 dark:hover:bg-slate-900/60"
    };
    return colors[color] || colors.slate;
  };

  const getIconColor = (color) => {
    const colors = {
      purple: "text-purple-600 dark:text-purple-300",
      orange: "text-orange-600 dark:text-orange-300", 
      green: "text-green-600 dark:text-green-300",
      slate: "text-slate-600 dark:text-slate-300"
    };
    return colors[color] || colors.slate;
  };

  return (
    <div className="space-y-6">
      {/* Required configuration badges */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-slate-700 dark:text-slate-200">Select configuration</h3>
        <div className="flex flex-wrap gap-2" data-tour="presets-state-selection">
          {["qa", "planning_pathway", "document_checklist", "application"].map((stateKey) => (
            <button
              key={stateKey}
              className={`px-3 py-1.5 text-xs rounded border ${selectedState === stateKey ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-200" : "bg-white border-slate-200 text-slate-700 dark:bg-slate-900/60 dark:border-slate-700 dark:text-slate-200"}`}
              onClick={() => {
                setSelectedState(stateKey);
                applyConfigPreset({ state: stateKey });
              }}
            >
              {stateKey.replace("_", " ")}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2" data-tour="presets-auth-selection">
          <button
            className={`px-3 py-1.5 text-xs rounded border ${selectedAuth === "anonymous" ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-200" : "bg-white border-slate-200 text-slate-700 dark:bg-slate-900/60 dark:border-slate-700 dark:text-slate-200"}`}
            onClick={() => setSelectedAuth("anonymous")}
            title="Anonymous"
          >
            <Globe className="inline h-3 w-3 mr-1" /> Anonymous
          </button>
          <button
            className={`px-3 py-1.5 text-xs rounded border ${selectedAuth === "logged_in" ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-200" : "bg-white border-slate-200 text-slate-700 dark:bg-slate-900/60 dark:border-slate-700 dark:text-slate-200"}`}
            onClick={() => setSelectedAuth("logged_in")}
            title="Logged In"
          >
            <Lock className="inline h-3 w-3 mr-1" /> Logged In
          </button>
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">Choose a Configuration Preset</h3>
        <p className="text-slate-600 dark:text-slate-400">Pick a council column, then choose an address</p>
      </div>

      {/* Three columns grouped by council; require state + auth selection first */}
      <div className="grid md:grid-cols-3 gap-4" data-tour="presets-address-selection">
        {['Blacktown', 'Cumberland', 'Wingecarribee'].map((council) => (
          <div key={council} className="space-y-2">
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{council}</h4>
            <div className="space-y-3">
              {presets.filter(p => p.title === council).map((preset) => {
                const Icon = preset.icon;
                const canSelect = Boolean(selectedState) && Boolean(selectedAuth);
                return (
                  <Card 
                    key={preset.id} 
                    className={`transition-all duration-200 ${getColorClasses(preset.color)} border-2 ${canSelect ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                    onClick={() => { if (canSelect) handlePresetSelect(preset); }}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg bg-white/80 dark:bg-slate-800/80 ${getIconColor(preset.color)}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">{preset.description}</CardTitle>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className="text-xs bg-white/60 dark:bg-slate-800/60 dark:border-slate-700">Address preset</Badge>
                            {!canSelect && (
                              <Badge variant="outline" className="text-xs bg-white/60 dark:bg-slate-800/60 dark:border-slate-700">Select state and login above</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
